import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, ArrowRight, Share2, Link2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { ARTICLE_DISCLAIMER } from "../data/content";

export default function ArticlePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="pt-40 pb-32 text-center text-slate-500 text-sm" data-testid="article-loading">Loading article…</div>;
  }

  if (!data) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="article-not-found">
        <h1 className="font-serif text-3xl font-semibold text-navy">Article not found</h1>
        <Link to="/knowledge-centre" className="mt-4 inline-flex items-center gap-2 text-gold font-semibold text-sm" data-testid="article-back-link">
          <ArrowLeft size={15} /> Back to Knowledge Centre
        </Link>
      </div>
    );
  }

  const { article, related } = data;
  const shareUrl = window.location.href;
  const shareLinks = [
    { label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(article.title + " — " + shareUrl)}`, testid: "share-whatsapp" },
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, testid: "share-linkedin" },
    { label: "X", href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`, testid: "share-x" },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, testid: "share-facebook" },
  ];

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => toast.success("Article link copied."));
  };

  return (
    <div data-testid="article-page">
      <SEO title={article.title} description={article.excerpt} path={`/knowledge-centre/${article.slug}`} />

      <article className="pt-32 sm:pt-40 pb-20 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal>
            <Link to="/knowledge-centre" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-gold transition-colors" data-testid="article-back-to-list">
              <ArrowLeft size={15} /> Knowledge Centre
            </Link>
            <div className="mt-8 flex items-center gap-3 text-xs">
              <span className="font-mono uppercase tracking-wider text-gold font-medium">{article.category}</span>
              <span className="w-1 h-1 rounded-full bg-slate-300" />
              <span className="text-slate-400">{format(new Date(article.created_at), "dd MMMM yyyy")}</span>
            </div>
            <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight tracking-tight" data-testid="article-title">
              {article.title}
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed border-l-2 border-gold pl-5" data-testid="article-excerpt">
              {article.excerpt}
            </p>
          </Reveal>

          {article.image_url && (
            <Reveal delay={0.1} className="mt-10">
              <div className="rounded-2xl overflow-hidden aspect-[16/9] shadow-lg">
                <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
              </div>
            </Reveal>
          )}

          <Reveal delay={0.15} className="mt-10">
            <div className="space-y-6" data-testid="article-content">
              {article.content.split(/\n\n+/).map((para, i) => (
                <p key={i} className="text-slate-700 leading-[1.85] text-[15px] sm:text-base">{para}</p>
              ))}
            </div>
          </Reveal>

          <div className="mt-10 rounded-xl bg-slate-100 border border-slate-200 p-6" data-testid="article-disclaimer">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-2">Disclaimer</p>
            <p className="text-xs text-slate-500 leading-relaxed">{ARTICLE_DISCLAIMER}</p>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="article-share">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <Share2 size={14} className="text-gold" /> Share
            </span>
            {shareLinks.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={s.testid}
                className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-gold hover:text-gold transition-colors"
              >
                {s.label}
              </a>
            ))}
            <button
              onClick={copyLink}
              data-testid="share-copy-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-gold hover:text-gold transition-colors"
            >
              <Link2 size={13} /> Copy Link
            </button>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-16 bg-slate-100" data-testid="related-articles">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-navy mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((a) => (
                <Link
                  key={a.article_id}
                  to={`/knowledge-centre/${a.slug}`}
                  data-testid={`related-article-${a.slug}`}
                  className="group rounded-xl border border-slate-200 bg-white p-6 hover:border-gold/50 hover:shadow-lg transition-all"
                >
                  <span className="text-xs font-mono uppercase tracking-wider text-gold">{a.category}</span>
                  <h3 className="mt-3 font-serif text-lg font-semibold text-navy group-hover:text-gold transition-colors leading-snug">{a.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
                    Read <ArrowRight size={12} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-14 bg-navy text-white" data-testid="article-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold">Have a question about this topic?</h2>
          <p className="mt-3 text-slate-300 text-sm">Speak with CA Neeraj Battan for advice specific to your situation.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="article-consult-button" className="rounded-full bg-gold px-6 py-3 text-sm font-bold text-navy hover:bg-gold-bright transition-colors">
              Book a Consultation
            </Link>
            <a href={`https://wa.me/919069107777`} target="_blank" rel="noopener noreferrer" data-testid="article-whatsapp-button" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold hover:bg-white/10 transition-colors">
              <MessageCircle size={15} className="text-gold" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
